import {
    Button,
    ButtonStyles,
    Dropdown,
    Icon,
    IconButton,
    Icons,
    Label,
    ListItem,
    RadioGroup,
    Search,
    Tooltip
} from '@silverfort/sf-components';
import { useState } from 'react';
import {
    AdTypeFilter,
    calcItemSubtitle,
    calcItemTitle,
    calcResourceIcon,
    ResourcesTypes,
    ACCOUNT_TYPE_TO_DISPLAY
} from './ResourcePickerConsts';
import IPSearch from '../IPSearch/IPSearch';
import Loader from '../Loader/Loader';

const shouldShowRemoveButton = (selectedResourceType, isSelected, onRemove) => {
    if (selectedResourceType === ResourcesTypes.IPs) {
        return !!onRemove;
    }
    return !!isSelected && !!onRemove;
};
const ResourcePicker = ({
    title,
    resources = [],
    ips = [],
    selectedResources = [],
    selectedIPs = [],
    showAdTypeFilter,
    showAddAll,
    hideNewIPButton,
    loading,
    onSearchChange,
    onAdTypeChange,
    onSelect,
    onClose,
    onAction,
    onAddAll,
    shouldShowTypeDisplay = false,
    className = '',
    shouldAddSubnet = false,
    itemTooltipText = '',
    onRemove = undefined,
    shouldShowItemTooltip = () => false,
    shouldDisableButton = () => false
}) => {
    const [searchTerm, setSearchTerm] = useState();
    const [isFullPathDisplay, setIsFullPathDisplay] = useState(false);
    const [selectedAdTypes, setSelectedAdTypes] = useState();
    const [selectedResourceType, setSelectedResourceType] = useState(ResourcesTypes.Resources);

    const data = selectedResourceType === ResourcesTypes.Resources ? resources : ips;
    const IPSelected = selectedResourceType === ResourcesTypes.IPs;

    const validateIP = (ip) => {
        const regex =
            /^(25[0-5]|2[0-4][0-9]|[0-1]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[0-1]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[0-1]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[0-1]?[0-9][0-9]?)(\/(3[0-2]|[1-2]?[0-9]))?$/;
        return regex.test(ip);
    };

    return (
        <div
            className={`h-[420px] w-[516px] rounded-lg border border-sf-gray-100 bg-sf-white shadow-sf-medium font-sf ${className}`}>
            <div className="flex border-b px-4 border-sf-gray-100 justify-between items-center h-14">
                <Label text={title} />
                <IconButton onClick={() => onClose?.()} icon={Icons.Cancel} />
            </div>
            <div className="h-14 px-4 flex border-b border-sf-gray-100 justify-between items-center">
                <RadioGroup
                    options={Object.keys(ResourcesTypes).map((optionKey) => ({
                        title: optionKey,
                        value: ResourcesTypes[optionKey]
                    }))}
                    onChange={(value) => {
                        setSelectedResourceType(value);
                        setSearchTerm('');
                    }}
                    selectedOption={selectedResourceType}
                />
                {!IPSelected && (
                    <div className="flex items-center gap-2">
                        {showAdTypeFilter && (
                            <Dropdown
                                {...AdTypeFilter}
                                items={AdTypeFilter.options}
                                key={AdTypeFilter.id}
                                selectedItems={selectedAdTypes || []}
                                removable={!!selectedAdTypes?.length}
                                onRemove={() => {
                                    setSelectedAdTypes([]);
                                    onAdTypeChange?.(AdTypeFilter.options.map((type) => type.value));
                                }}
                                onChange={(val) => {
                                    setSelectedAdTypes(val);
                                    onAdTypeChange?.(val);
                                }}
                            />
                        )}
                        {shouldShowTypeDisplay && (
                            <div>
                                <Dropdown
                                    className="h-[32px] w-[128px] "
                                    items={ACCOUNT_TYPE_TO_DISPLAY}
                                    selectedItems={[isFullPathDisplay ? 'full' : 'display']}
                                    onChange={(val) => {
                                        setIsFullPathDisplay(val === 'full');
                                    }}
                                    variant="trigger"
                                    width="128px"
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>
            <div className="h-10 px-4 w-full border-b border-sf-gray-100 flex items-center">
                {IPSelected ? (
                    <IPSearch
                        className="w-full"
                        shouldAddSubnet={shouldAddSubnet}
                        ip={searchTerm}
                        onChange={(val) => {
                            setSearchTerm(val);
                            onSearchChange?.(val, selectedResourceType);
                        }}
                    />
                ) : (
                    <Search
                        className="w-full"
                        width="100%"
                        text={searchTerm}
                        onChange={(val) => {
                            setSearchTerm(val);
                            onSearchChange?.(val, selectedResourceType);
                        }}
                    />
                )}
            </div>
            <div className="h-[212px] px-4 py-2 border-b border-sf-gray-100 flex flex-col w-full overflow-auto">
                {IPSelected && !hideNewIPButton && !!searchTerm?.length && (
                    <div className="h-12 w-full p-2">
                        <Button
                            disabled={!validateIP(searchTerm)}
                            iconLeft={Icons.Add}
                            text={`"${searchTerm}"`}
                            onClick={() => {
                                onSelect(searchTerm, selectedResourceType);
                                setSearchTerm();
                                onSearchChange('', selectedResourceType);
                            }}
                            buttonStyle={ButtonStyles.Primary}
                            className="w-full justify-stretch"
                        />
                    </div>
                )}
                {!loading && data.length ? (
                    data.map((item, i) => {
                        const isSelected = IPSelected
                            ? selectedIPs.find((ip) => ip === item)
                            : selectedResources.find((resource) =>
                                  resource.identifier_type === 'name'
                                      ? resource.identifier.toLowerCase() ===
                                        (item.name || item.identifier).toLowerCase()
                                      : resource.guid === (item.guid || item.identifier)
                              );
                        return (
                            <Tooltip placement="top-start" title={shouldShowItemTooltip(item) ? itemTooltipText : ''}>
                                <div
                                    key={i}
                                    className="flex items-center px-2 rounded-lg justify-between hover:bg-sf-gray-50">
                                    <ListItem
                                        onRemove={
                                            shouldShowRemoveButton(selectedResourceType, isSelected, onRemove)
                                                ? () => onRemove?.(item, selectedResourceType)
                                                : undefined
                                        }
                                        disabled={isSelected || shouldDisableButton(item)}
                                        onClick={() => onSelect?.(item, selectedResourceType)}
                                        IconLeft={calcResourceIcon(item, IPSelected)}
                                        IconRight={
                                            item.detectedBySilverfort
                                                ? () => (
                                                      <Icon icon={Icons.CircleCheck} className="text-sf-primary-500" />
                                                  )
                                                : undefined
                                        }
                                        title={calcItemTitle(item, IPSelected, isFullPathDisplay)}
                                        subtitle={calcItemSubtitle(item)}
                                    />
                                    {isSelected && (
                                        <div className="flex w-fit items-center h-6 bg-sf-gray-100 px-2 text-12 font-medium text-sf-gray-500">
                                            Added
                                        </div>
                                    )}
                                </div>
                            </Tooltip>
                        );
                    })
                ) : (
                    <div className="h-full w-full flex justify-center items-center text-12 text-sf-gray-500 ">
                        {loading ? <Loader /> : IPSelected ? 'Search or add an IP address' : 'No results found'}
                    </div>
                )}
            </div>
            <div className="h-14 gap-2 px-4 flex justify-end items-center py-4 px-2">
                {showAddAll && (
                    <Button
                        iconLeft={Icons.CircleAdd}
                        text="Add All Detected"
                        buttonStyle={ButtonStyles.Primary}
                        onClick={() => onAddAll?.()}
                    />
                )}
                <Button text="Done" onClick={() => onAction?.()} />
            </div>
        </div>
    );
};

export default ResourcePicker;
